import api, { USE_MOCK, setAccessToken, refreshSession } from './apiClient';
import mockApi from '../mock/backend';

export const authService = {
  /**
   * Restore an access token from the refresh cookie before the first API call.
   * A no-op in mock mode, where there are no tokens.
   */
  async bootstrap() {
    if (USE_MOCK) return true;
    try {
      await refreshSession();
      return true;
    } catch {
      return false;
    }
  },

  /** The few settings the sign-in screen needs before anybody has signed in. */
  async publicConfig() {
    if (USE_MOCK) return { allowMicrosoftSso: true };
    try {
      return await api.get('/public/config');
    } catch {
      // Never let this block sign-in; the optional button simply stays hidden.
      return { allowMicrosoftSso: false };
    }
  },

  /** Public: what an invitation link points at. */
  resolveInvitation(token) {
    if (USE_MOCK) return Promise.resolve({ state: 'invalid' });
    return api.get(`/public/invitations/${token}`);
  },

  acceptInvitation(token, password) {
    if (USE_MOCK) return Promise.resolve({ ok: true });
    return api.post(`/public/invitations/${token}/accept`, { password });
  },

  /** Public: is this password-reset link still usable? Never says whose account it is. */
  resolvePasswordReset(token) {
    if (USE_MOCK) return Promise.resolve({ state: 'invalid' });
    return api.get(`/public/password-reset/${token}`);
  },

  completePasswordReset(token, password) {
    if (USE_MOCK) return Promise.resolve({ ok: true });
    return api.post(`/public/password-reset/${token}`, { password });
  },

  async login(credentials) {
    if (USE_MOCK) return mockApi.login(credentials);
    const result = await api.post('/auth/login', credentials);
    if (result.accessToken) setAccessToken(result.accessToken);
    return result;
  },

  async verifyMfa(payload) {
    if (USE_MOCK) return mockApi.verifyMfa(payload);
    const result = await api.post('/auth/mfa', payload);
    if (result.accessToken) setAccessToken(result.accessToken);
    return result;
  },

  /** Ask for a fresh one-time code on the same challenge. */
  async resendMfa(challengeId) {
    if (USE_MOCK) return mockApi.resendMfa(challengeId);
    return api.post('/auth/mfa/resend', { challengeId });
  },

  /** What this account has set up: authenticator, verified number, recovery codes left. */
  mfaStatus() {
    if (USE_MOCK) return mockApi.mfaStatus();
    return api.get('/auth/mfa/status');
  },

  /** Forget the authenticator so the next sign-in enrols a new one (a replaced phone). */
  resetAuthenticator(password) {
    if (USE_MOCK) return mockApi.resetAuthenticator();
    return api.post('/auth/mfa/reset', { password });
  },

  /** Replace the recovery codes; the previous set stops working. */
  regenerateRecoveryCodes(password) {
    if (USE_MOCK) return mockApi.regenerateRecoveryCodes();
    return api.post('/auth/mfa/recovery-codes', { password });
  },

  /** Send a WhatsApp code to prove a number belongs to this account. */
  startPhoneVerification(phone) {
    if (USE_MOCK) return mockApi.startPhoneVerification(phone);
    return api.post('/auth/phone/start', { phone });
  },

  confirmPhoneVerification(payload) {
    if (USE_MOCK) return mockApi.confirmPhoneVerification(payload);
    return api.post('/auth/phone/confirm', payload);
  },

  async logout() {
    if (USE_MOCK) return mockApi.logout();
    setAccessToken(null);
    return api.post('/auth/logout');
  },

  async me() {
    if (USE_MOCK) return mockApi.me();
    return api.get('/users/me');
  },

  async forgotPassword(email) {
    if (USE_MOCK) return new Promise((r) => setTimeout(() => r({ ok: true }), 500));
    return api.post('/auth/forgot-password', { email });
  },

  async updateProfile(patch) {
    if (USE_MOCK) return mockApi.updateProfile(patch);
    return api.patch('/auth/profile', patch);
  },

  listSessions() {
    if (USE_MOCK) return mockApi.listSessions();
    return api.get('/users/me/sessions');
  },

  revokeSession(sessionId) {
    if (USE_MOCK) return mockApi.revokeSession(sessionId);
    return api.delete(`/users/me/sessions/${sessionId}`);
  },

  changePassword(payload) {
    if (USE_MOCK) return mockApi.changePassword(payload);
    return api.post('/auth/change-password', payload);
  },

  /** Demo only — impersonates a seeded account to demonstrate permission-aware UI. */
  async switchUser(userId) {
    if (USE_MOCK) return mockApi.switchUser(userId);
    throw new Error('User switching is only available in demo mode.');
  },

  demoAccounts() {
    return USE_MOCK ? mockApi.demoAccounts() : [];
  },

  ssoUrl() {
    return `${import.meta.env.VITE_API_BASE_URL || '/api'}/auth/sso/microsoft`;
  },
};

export default authService;
